import React, {useEffect, useState} from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation,
} from 'react-router-dom';
import {
  View,
  Text,
  SafeAreaView,
  StyleSheet,
  Linking,
  Dimensions,
} from 'react-native';

import networkService from '../Network/NetworkManager';
import Appload from '../HelperClasses/AppLoaderClasses/AppLoader';
import AppConstants from '../Constants/AppConstants';
import FeatureUpgradePanel from '../Component/FeatureUpgradePanel';
import {
  saveAdminData,
  saveUserData,
  clearStorage,
} from '../HelperClasses/UserPrefrences';
import {getProductAccess, getQueryParam} from '../HelperClasses/SingletonClass';
import {setupUserPropertyOnMixpanel} from '../Models/MixPannelEnum';
import AppColor from '../HelperClasses/AppColor';
import Error from '../Constants/Error';
import {getSelectedTenantDetails} from '../Constants/Functions';
import {CONFIG_CONSTANT, set_root_configs} from '../Constants/ConfigsConstants';
import Error404 from '../Component/Error/Error404';
import MediaPickerPage from '../MediaManager/MediaPickerPage';
import MainRootView from './MainRootView';
import PeoplePage from '../HR/People/PeoplePage';
import getTenantCurrency from '../Actions/getTenantCurrencies';
import getTenantLanguages from '../Actions/getTenantLanguages';
import {RestrictedFeatureEnum} from '../Models/RestrictedFeatureEnum';
import {MODULEKEYS} from '../Models/Enum';
import ComponentErrorFetch from '../Constants/CompoentErrorFetch';
import {loadSubscriptionEntitlements} from '../Services/SubscriptionEntitlements';
import {RoleProtectedPage} from './RoleProtectedOutlet';
import {setSentryContext} from '../Monitoring/sentry';

// Page-level chunks keep the initial SuperAdmin bundle focused on the shell and
// landing experience. Each feature downloads only when its route is opened.
const Reports = React.lazy(() => import('../Report/Report'));
const SearchAnalytics = React.lazy(() => import('../Report/SearchAnalytics'));
const PlatformHealth = React.lazy(() => import('../PlatformHealth/PlatformHealth'));
const MapsPage = React.lazy(() => import('../Maps/MapsPage'));
const ChatInboxPage = React.lazy(() => import('../Chats/ChatInboxPage'));
const SocialPage = React.lazy(() => import('../Social/SocialPage'));
const Intelligence = React.lazy(() => import('../Intelligence/Intelligence'));
const OnboardingMay = React.lazy(() => import('../Dashboard/OnboardingMay'));
const MobileAppConfig = React.lazy(() => import('../Settings/MobileAppConfig/MobileAppConfig'));
const Orders = React.lazy(() => import('../Orders/Orders'));
const CreateOrder = React.lazy(() => import('../Orders/CreateOrder/CreateOrder'));
const Inventory = React.lazy(() => import('../MainListing/Inventory'));
const InventoryOperationsPage = React.lazy(() => import('../MainListing/Inventory/InventoryOperationsPage'));
const PurchaseOrdersPage = React.lazy(() => import('../MainListing/PurchaseOrders/PurchaseOrdersPage'));
const LocationsPage = React.lazy(() => import('../MainListing/Locations/LocationsPage'));
const SuppliersPage = React.lazy(() => import('../MainListing/Suppliers/SuppliersPage'));
const SupplierDetailPage = React.lazy(() => import('../MainListing/Suppliers/SupplierDetailPage'));
const Transaction = React.lazy(() => import('../Transactions/Transactions'));
const Setting = React.lazy(() => import('../Settings/Settings'));
const Collections = React.lazy(() => import('../Collections/AllCollection'));
const MainListings = React.lazy(() => import('../MainListing/MainListing'));
const AccountLists = React.lazy(() => import('../Accounts/AccountList'));
const PromoBanners = React.lazy(() => import('../PromoBanner/PromoBanner'));
const ReviewRatings = React.lazy(() => import('../ReviewRate/ReviewRating'));
const MediaManagers = React.lazy(() => import('../MediaManager/MediaManager'));
const NativeIntegrations = React.lazy(() => import('../Settings/NativeIntegrations/NativeIntegrations'));
const AppTranslation = React.lazy(() => import('../Settings/AppTranslation/AppTranslation'));
const Notifications = React.lazy(() => import('../Settings/Notifications/Notifications'));
const Communication2 = React.lazy(() => import('../Settings/Notifications/Communication2'));
const Coupons = React.lazy(() => import('../Coupons/Coupons'));
const CouponDetail = React.lazy(() => import('../Coupons/CouponDetail'));
const CategoryList = React.lazy(() => import('../Settings/Listings/CategoryList'));
const AttributesList = React.lazy(() => import('../Settings/Listings/AttributesList'));
const ListingVariantType = React.lazy(() => import('../Settings/Listings/ListingVariantType'));
const VariantTypeDetail = React.lazy(() => import('../Settings/Listings/VariantTypeDetail'));
const AccountSetting = React.lazy(() => import('../Settings/GlobalSettings/AccountSetting'));
const Users = React.lazy(() => import('../Users/Users'));
const Commision = React.lazy(() => import('../RevenueModel/Commissions'));
const RewardProgramsPage = React.lazy(() => import('../Rewards/RewardProgramsPage'));
const RewardProgramDetailPage = React.lazy(() => import('../Rewards/RewardProgramsPage').then(module => ({default: module.RewardProgramDetailPage})));
const BundlesPage = React.lazy(() => import('../Bundles/BundlesPage'));
const ShippingMethod = React.lazy(() => import('../Settings/ShippingMethod/ShippingMethods'));
const PaymentMethod = React.lazy(() => import('../Settings/PaymentMethod/PaymentMethods'));
const Currencies = React.lazy(() => import('../Currency/ListCurrencies'));
const Developers = React.lazy(() => import('../Settings/Developer/DeveloperMain'));
const McpConnectionsPage = React.lazy(() => import('../Settings/Developer/McpConnectionsPage'));
const McpLogsPage = React.lazy(() => import('../Settings/Developer/McpLogsPage'));
const OpenSourceStarterKitsPage = React.lazy(() => import('../Channels/OpenSourceStarterKitsPage'));
const WorkforcePage = React.lazy(() => import('../HR/Workforce/WorkforcePage'));
const PayrollPage = React.lazy(() => import('../HR/Payroll/PayrollPage'));
const ExpensesPage = React.lazy(() => import('../HR/Expenses/ExpensesPage'));
const RecurringPaymentsPage = React.lazy(() => import('../HR/Expenses/RecurringPaymentsPage'));
const Auth = React.lazy(() => import('../Settings/Auth/Auth'));
const SpecialFees = React.lazy(() => import('../Settings/SpecialFees/SpecialFeesContainer'));
const LanguageLists = React.lazy(() => import('../Settings/Languages/LanguageList'));
const AppCountry = React.lazy(() => import('../Settings/AppCountries/AppCountries'));
const Checkouts = React.lazy(() => import('../Settings/Checkout/Checkout'));
const Taxes = React.lazy(() => import('../Settings/Taxes/Taxes'));
const AddListings = React.lazy(() => import('../MainListing/AddListings'));
const ListingDetail2 = React.lazy(() => import('../MainListing/ListingDetail2'));
const AccountDetail2 = React.lazy(() => import('../Accounts/AccountDetail2'));
const UserDetail2 = React.lazy(() => import('../Users/UserDetail2'));
const AddAttributes2 = React.lazy(() => import('../Settings/Listings/AddAttributes2'));
const AddPromoBanner = React.lazy(() => import('../PromoBanner/AddPromoBanner'));
const AddCategory2 = React.lazy(() => import('../Settings/Listings/AddCategory2'));
const ReportedListings = React.lazy(() => import('../ReportedListings/ReportedListings'));
const CreateCollection2 = React.lazy(() => import('../Collections/CreateCollection2'));
const CollectionDetail = React.lazy(() => import('../Collections/CollectionDetail'));
const OrderDetails2 = React.lazy(() => import('../Orders/OrderDetails2'));
const OrderSuccess = React.lazy(() => import('../Orders/OrderSuccess'));
const PersonalAssistant = React.lazy(() => import('../Dashboard/PersonalAssistant'));
const CategoryList2 = React.lazy(() => import('../Settings/Listings/CategoryList2'));
const CategoryDetail = React.lazy(() => import('../Settings/Listings/CategoryDetail'));
const CategoryDescriptionTranslate = React.lazy(() => import('../Settings/Listings/CategoryDescriptionTranslate'));
const ListingsConfiguration = React.lazy(() => import('../Settings/GlobalSettings/Listing/ListingsConfiguration'));
const SubscriptionLayout = React.lazy(() => import('../Settings/Subscription/SubscriptionLayout'));
const SubscriptionDetail = React.lazy(() => import('../Settings/Subscription/SubscriptionDetail'));
const MembershipLayout = React.lazy(() => import('../Settings/Membership/MembershipLayout'));
const WhatsAppSettingsPage = React.lazy(() => import('../WhatsApp/WhatsAppSettingsPage'));
const AttributesGroups = React.lazy(() => import('../Settings/Attributes/AttributesGroups'));
const ManageLeftNavBarConfiguration = React.lazy(() => import('../Settings/GlobalSettings/ConfigurationSetup/NavbarConfiguration'));
const ExtensionLayout = React.lazy(() => import('../Settings/Extension/ExtensionLayout'));
const AddListing = React.lazy(() => import('../AddListing'));
const IntegrationPage = React.lazy(() => import('./IntegrationPage'));
const AutomationRulesPage = React.lazy(() => import('../AutomationRules/AutomationRulesPage'));
const Test = React.lazy(() => import('../Test/Test'));
const ParentTest = React.lazy(() => import('../Test/ParentTest'));
const SentryCheck = React.lazy(() => import('../SentryCheck/SentryCheck'));
const GamificationTestPage = React.lazy(() => import('../Gamification/GamificationTestPage'));
const CmsTestPage = React.lazy(() => import('../CMS/CmsTestPage'));
const CMSAddPage = React.lazy(() => import('../CMS/CMSAddPage'));
const CMSListPage = React.lazy(() => import('../CMS/CMSListPage'));
const CMSEditPage = React.lazy(() => import('../CMS/CMSEditPage'));
// Temporarily disabled while the Whale catalog is being aligned with the
// shared marketplace catalog used by tradly.app.
// const WorkspaceCatalogPage = React.lazy(() => import('../Workspace/WorkspaceCatalogPage'));
const ScheduleCalendar = React.lazy(() => import('../Schedule/ScheduleCalendar'));
const UpgradePreview = React.lazy(() => import('../Component/UpgradePreview'));
const UpgradePage = React.lazy(() => import('../Component/UpgradePage'));
const UsageBreakdown = React.lazy(() => import('../Component/UsageBreakdown'));
const ComplianceAuditLogs = React.lazy(() => import('../Settings/AuditLogs/ComplianceAuditLogs'));
const InviteMemberPage = React.lazy(() => import('../WorkspacePort/WorkspacePort').then(module => ({default: module.InviteMemberPort})));
const BusinessSettingsPage = React.lazy(() => import('../WorkspacePort/WorkspacePort').then(module => ({default: module.AccountSettingPort})));
const WorkspacePrivacyPage = React.lazy(() => import('../WorkspacePort/WorkspacePort').then(module => ({default: module.PrivacyPort})));
const DomainSettingsPage = React.lazy(() => import('../WorkspacePort/WorkspacePort').then(module => ({default: module.DomainSettingPort})));
const InvoicesPage = React.lazy(() => import('../WorkspacePort/WorkspacePort').then(module => ({default: module.InvoicesPort})));
const PlansPage = React.lazy(() => import('../WorkspacePort/WorkspacePort').then(module => ({default: module.PlansPort})));
const AccountingPage = React.lazy(() => import('../Accounting/AccountingPage'));
// Workspace account tools are available as direct routes as well as through
// the native navigation flow. Keep them unlisted here for now; the workspace
// sidebar can be updated separately once the web presentation is approved.

const LegacyAddListingRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: '/create-listing',
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

// Wrapper component to force remount on integration ID change
const IntegrationPageWrapper = () => {
  const location = useLocation();

  return React.createElement(IntegrationPage, {
    key: location.pathname + location.hash,
  });
};

const APPURL = require('../Constants/URLConstants');
const windowWidth = Dimensions.get('window').width;

let fixedWidth = AppConstants.mobileMaxWidth;
let itsMobileView = windowWidth < fixedWidth ? true : false;

// Pages a workspace can always open while it still has to choose a plan.
const PLAN_GATE_EXEMPT_PATHS = ['/upgrade', '/workspace/plans', '/workspace/account', '/workspace/invoices', '/accounting'];

const AllRoutes = () => {
  const STARTUP_SHELL_TIMEOUT = 10000;
  // Set when Skua says this workspace must choose a paid plan before continuing.
  const [requiresPlanSelection, setRequiresPlanSelection] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [root_state, setRoot_state] = useState({
    loggedIn: '',
    reload: false,
    dataLoad: false,
    isVisible: false,
    onboardingCompleted: false,
    inviteMember: false,
    moveToLandingPage: false,
    subscribed: false,
    configReady: false,
    // Keep the sidebar behind its loading shell until tenant visibility
    // settings arrive, preventing the full-menu flash during bootstrap.
    sideMenuConfigReady: false,
    rootViewComponent: itsMobileView ? 1 : 3,
  });

  // states
  const {
    loggedIn,
    reload,
    dataLoad,
    isVisible,
    onboardingCompleted,
    inviteMember,
    moveToLandingPage,
    subscribed,
    configReady,
    sideMenuConfigReady,
    rootViewComponent,
  } = root_state;

  // effects
  useEffect(() => {
    AppConstants.AdminSessionBootstrapComplete = false;
    const startupTimeout = setTimeout(() => {
      // A slow optional/bootstrap request should never leave the whole app
      // behind the global loader. The shell can render while those requests
      // continue to populate their shared constants.
      setRoot_state(prevState => ({
        ...prevState,
        configReady: true,
        dataLoad: true,
      }));
    }, STARTUP_SHELL_TIMEOUT);

    const fetch_data = async () => {
      try {
        const responseJson = await networkService.networkCall(
          APPURL.URLPaths.clients,
          'get',
          '',
          AppConstants.BearerForTantantsAndUserDetail,
        );
        if (responseJson['status'] == true) {
          var configs = getSelectedTenantDetails(responseJson['data']['tenants']);
          const selectedConfig = configs?.[0];
          if (!selectedConfig) {
            return;
          }
          Linking.getInitialURL().then(url => {
            if (url) {
              const {hostname} = new URL(url);
              AppConstants.appMainURL = hostname;
            }
            let productAccess = selectedConfig['product_access'];
            AppConstants.isInternal = selectedConfig['internal'];
            let rsp = getProductAccess(productAccess, url);
            // this.state.moveToLandingPage = rsp;
          });
          // Set the in-memory bearer immediately. The tenant response is
          // already authenticated, so waiting for a second local-storage
          // read can leave pages such as Platform Health without a session.
          const adminKey = selectedConfig['admin_key'];
          AppConstants.BearerAuthorization = adminKey || '';
          await saveAdminData('adminKey', adminKey, 2);
          saveUserData('id', selectedConfig['id'], 4);
          // These calls do not depend on one another. Keeping them parallel
          // removes one full API round trip from the authenticated startup path.
          loadConfigApi();
          getLoggedInUserDetail();
        }
      } catch (error) {
        if (AppConstants.isDebug) {
          console.warn('Unable to complete tenant bootstrap:', error);
        }
      } finally {
        AppConstants.AdminSessionBootstrapComplete = true;
        window.dispatchEvent(
          new Event('tradly:admin-session-bootstrap-complete'),
        );
        setRoot_state(prevState => ({
          ...prevState,
          dataLoad: true,
        }));
      }
    };
    fetch_data();
    loadCurrencies();
    loadLanguages();
    //
    AppConstants.rootViewComponent = itsMobileView ? 1 : 3;

    //
    setRoot_state(prevState => ({
      ...prevState,
      dataLoad: true,
    }));
    //
    clearStorage('modules');
    clearStorage('role_full_access');
    clearStorage('role_strict_mode');

    return () => clearTimeout(startupTimeout);
  }, [0]);

  // The shell can render as soon as tenant configuration is ready. Plan
  // restrictions continue loading in the background so a slow plans request
  // does not block the whole admin page behind the global loader.
  useEffect(() => {
    if (configReady) {
      setRoot_state(prevState => ({
        ...prevState,
        reload: true,
        isVisible: true,
      }));
    }
  }, [configReady]);

  //
  const initializeMixPanel = () => {
    setupUserPropertyOnMixpanel();
  };

  const setupLogrocket = () => {
    Promise.all([import('logrocket'), import('logrocket-react')]).then(
      ([{default: LogRocket}, {default: setupLogRocketReact}]) => {
        LogRocket.init(AppConstants.logRocketKey);
        LogRocket.identify(AppConstants.userFullName, {
          name: AppConstants.userFullName,
        });
        setupLogRocketReact(LogRocket);
      },
    );
  };

  // load currencies
  const loadCurrencies = async () => {
    const currencies = await getTenantCurrency();
    AppConstants.currencies = currencies;
  };

  // load languages
  const loadLanguages = async () => {
    const languages = await getTenantLanguages();
    AppConstants.languages = languages;
  };

  //
  const loadConfigApi = async () => {
    try {
      const responseJson = await networkService.networkCall(
        APPURL.URLPaths.config + '?key_group=onboarding,general',
        'get',
      );
      if (responseJson['status'] == true) {
        var configs = responseJson['data']['configs'];
        AppConstants.siteURL = configs['site_url'] ?? '';
        clientAllDomains();
        loadSuperAdminSetupConfigApi();
        loadAllConfigGroupApi();
        // this.setState({
        //   onboardingCompleted: configs['onboarding_completed'] || false,
        // });
        AppConstants.appType = configs['sub_type'] == 1 ? false : true;
        AppConstants.moduleType = configs['type'] || 1;
      }
    } finally {
      // Config is useful to the shell, but it is not a reason to block every
      // route indefinitely when the endpoint is unavailable.
      setRoot_state(prevState => ({
        ...prevState,
        configReady: true,
        dataLoad: true,
      }));
    }
  };

  //Load all configs group wise
  const loadConfigsByGroup = async () => {
    const responseJson = await networkService.networkCall(
      `${APPURL.URLPaths.config_list_group}?key_group=${AppConstants.all_groups_name}`,
      'get',
    );
    if (responseJson['status'] == true) {
      var configs = responseJson['data']['configs'];
      AppConstants.ALL_CONFIGS = configs['site_url'] ?? '';

      // this.setState({reload: true });
    }
    setRoot_state(prevState => ({
      ...prevState,
      dataLoad: true,
    }));
  };

  //
  const loadAllConfigGroupApi = async () => {
    const responseJson = await networkService.networkCall(
      APPURL.URLPaths.group_configs_list +
        `general,onboarding,accounts,listings,integrations,payments,seo,social,address,orders,shipping`,
      'get',
    );
    if (responseJson['status'] == true) {
      var configs = responseJson['data']['configs'];
      set_root_configs({all_configs: configs});
    }
    setRoot_state(prevState => ({
      ...prevState,
      dataLoad: true,
    }));
  };

  //
  const loadSuperAdminSetupConfigApi = async () => {
    try {
      const responseJson = await networkService.networkCall(
        APPURL.URLPaths.group_configs_list + `superadmin`,
        'get',
      );
      if (responseJson['status'] == true) {
        var configs = responseJson['data']['configs'];
        CONFIG_CONSTANT.SUPERADMIN = configs?.superadmin ?? {};
      }
    } finally {
      setRoot_state(prevState => ({
        ...prevState,
        sideMenuConfigReady: true,
      }));
    }
    setRoot_state(prevState => ({
      ...prevState,
      dataLoad: true,
    }));
  };

  //
  const clientAllDomains = async () => {
    const responseJson = await networkService.networkCall(
      APPURL.URLPaths.domains,
      'get',
      '',
      AppConstants.BearerForTantantsAndUserDetail,
    );
    if (responseJson['status'] == true) {
      var domains = responseJson['data']['tenant_domains'];
      var primary_domain = domains.filter(d => d.primary);
      AppConstants.domainKey = primary_domain?.[0]?.['domain']
        ? `https://${primary_domain?.[0]?.['domain']}`
        : '';
      AppConstants.all_domains = domains ?? [];
    }
  };

  //
  const getLoggedInUserDetail = async () => {
    const responseJson = await networkService.networkCall(
      APPURL.URLPaths.skuaUser,
      'get',
      '',
      '',
    );
    if (responseJson['status'] == true) {
      var configs = responseJson['data']['user'];
      AppConstants.userId = configs['id'];
      AppConstants.userFullName = configs['name'] || '';
      AppConstants.userEmail = configs['email'] || '';
      AppConstants.userProfilePic = configs['profile_pic'] || '';
      setSentryContext();
      getTenantApi();
      setupLogrocket();
      // Permissions are optional during shell startup. A tenant can return
      // 412 while its role/modules configuration is being provisioned; that
      // must not become an unhandled rejection and break the app bootstrap.
      getUserRoleModule().catch(error => {
        if (AppConstants.isDebug) {
          console.warn('Unable to load user role modules:', error);
        }
        saveUserData('modules', [], 5);
        saveUserData('role_full_access', true, 5);
        saveUserData('role_strict_mode', false, 5);
      saveUserData('role_strict_mode', false, 5);
        if (AppConstants.isDebug) {
          setRoot_state(prevState => ({
            ...prevState,
            isVisible: true,
          }));
        }
      });
      getsubscriptionsPlans();
      initializeMixPanel();
    } else {
      setRoot_state(prevState => ({
        ...prevState,
        dataLoad: true,
      }));
    }
  };

  //
  const getUserRoleModule = async () => {
    let responseJson;
    try {
      responseJson = await networkService.networkCall(
        APPURL.URLPaths.userRole + `/${AppConstants.userId}/modules`,
        'get',
        '',
        '',
      );
    } catch (error) {
      // A missing/inaccessible module configuration should not prevent the
      // authenticated shell from rendering.
      saveUserData('modules', [], 5);
      saveUserData('role_full_access', true, 5);
      saveUserData('role_strict_mode', false, 5);
      throw error;
    }
    if (responseJson['status'] == true) {
      let modules = responseJson['data']['modules'];
      // Owner, or no module list without strict mode: full access (same rules as Skua /rbac/check).
      // Older Skua responses have no full_access: an empty module list meant full access.
      const fullAccess = typeof responseJson['data']['full_access'] === 'boolean'
        ? responseJson['data']['full_access']
        : !Array.isArray(modules) || modules.length === 0;
      saveUserData('role_full_access', fullAccess, 5);
      saveUserData('role_strict_mode', responseJson['data']['strict_mode'] === true, 5);
      if (AppConstants.isDebug) {
        setRoot_state(prevState => ({
          ...prevState,
          reload: true,
          isVisible: true,
        }));
      }
      saveUserData('modules', modules, 5);
    } else {
      // Module provisioning can temporarily return 412. Keep the shell
      // usable while treating the user as having no optional modules.
      saveUserData('modules', [], 5);
      setRoot_state(prevState => ({
        ...prevState,
        isVisible: true,
      }));
    }
  };

  //
  const getsubscriptionsPlans = async () => {
    await loadSubscriptionEntitlements();
    setRequiresPlanSelection(Boolean(AppConstants.requiresPlanSelection));
  };

  //
  const getTenantApi = async () => {
    const responseJson = await networkService.networkCall(
      APPURL.URLPaths.clients,
      'get',
      '',
      AppConstants.BearerForTantantsAndUserDetail,
    );

    if (responseJson['status'] == true) {
      var configs = getSelectedTenantDetails(responseJson['data']['tenants']);
      AppConstants.tenantName = `${configs[0]['id']}`;
      AppConstants.tenant_list = responseJson['data']['tenants'];
      AppConstants.tenant_metadata = configs[0]?.['metadata'];
      setSentryContext();
    }
    setRoot_state(prevState => ({
      ...prevState,
      dataLoad: true,
    }));
  };

  const ChangeRootView = componentNumber => {
    setRoot_state(prevState => ({
      ...prevState,
      rootViewComponent: componentNumber,
    }));
    AppConstants.rootViewComponent = componentNumber;
  };

  let router = createBrowserRouter([
    {
      path: '/media-picker',
      element: (
        <RoleProtectedPage moduleKey={MODULEKEYS.MEDIA}>
          <MediaPickerPage />
        </RoleProtectedPage>
      ),
      errorElement: <Error404 />,
    },
    {
      path: '/',
      element: <MainRootView sideMenuConfigReady={sideMenuConfigReady} />,
      errorElement: <Error404 />,
      children: [
        {
          errorElement: <ComponentErrorFetch />,
          children: [
            {
              index: true,
              element: <OnboardingMay />,
            },
            {
              path: 'dashboard',
              element: <OnboardingMay />,
            },
            {
              path: 'onboarding-may',
              element: <OnboardingMay />,
            },
            {
              path: 'personal-assistant',
              element: <PersonalAssistant />,
            },
            {
              path: 'workspace/user-management',
              element: <InviteMemberPage />,
            },
            {
              path: 'workspace/account',
              element: <BusinessSettingsPage />,
            },
            {
              path: 'workspace/privacy',
              element: <WorkspacePrivacyPage />,
            },
            {
              path: 'workspace/domain',
              element: <DomainSettingsPage />,
            },
            {
              path: 'workspace/plans',
              element: <PlansPage />,
            },
            {
              path: 'workspace/invoices',
              element: <InvoicesPage />,
            },
            {
              path: 'accounting',
              element: <AccountingPage />,
            },
            {
              path: 'reports',
              element: <Reports />,
            },
            {
              path: 'reports/search-analytics',
              element: <SearchAnalytics />,
            },
            {
              path: 'platform-health',
              element: <PlatformHealth />,
            },
            {
              path: 'maps',
              element: <MapsPage />,
            },
            {
              path: 'intelligence',
              element: <Intelligence />,
            },
            {
              path: 'orders',
              element: <Orders />,
            },
            {
              path: 'create-order',
              element: <CreateOrder />,
            },
            {
              path: 'inventory',
              element: <Inventory />,
            },
            {
              path: 'inventory/operations',
              element: <InventoryOperationsPage />,
            },
            {
              path: 'purchase-orders',
              element: <PurchaseOrdersPage />,
            },
            {
              path: 'stock-locations',
              element: <LocationsPage />,
            },
            {
              path: 'suppliers',
              element: <SuppliersPage />,
            },
            {
              path: 'suppliers/:supplierId',
              element: <SupplierDetailPage />,
            },
            {
              path: 'schedule',
              element: <ScheduleCalendar />,
            },
            // {
            //   path: 'order/:orderID',
            //   element: <OrderDetails />,
            // },
            {
              path: 'orders/:orderID',
              element: <OrderDetails2 />,
            },
            {
              path: 'order-success/:orderID',
              element: <OrderSuccess />,
            },
            {
              path: 'transaction',
              element: <Transaction />,
            },
            {
              path: 'app-customization',
              element: <MobileAppConfig />,
            },
            {
              path: 'app-integration',
              element: <NativeIntegrations />,
            },

            // {
            //   path: 'seo',
            //   element: <SEOSetting />,
            // },
            {
              path: 'strings-customisation',
              element: <AppTranslation />,
            },
            {
              path: 'communications',
              // Preserve the old URL while making the modern page canonical.
              element: <Navigate to="/communication-2" replace />,
            },
            {
              path: 'legacy-communications',
              element: <Notifications />,
            },
            {
              path: 'communication-2',
              element: <Communication2 />,
            },
            {
              path: 'chat-inbox',
              element: <ChatInboxPage />,
            },
            {
              path: 'social',
              element: <SocialPage />,
            },
            {
              path: 'accounts',
              element: AppConstants.appType ? <Error /> : <AccountLists />,
            },
            {
              path: 'account/:accountID',
              element: AppConstants.appType ? <Error /> : <AccountDetail2 />,
            },
            {
              path: 'account-category',
              element: AppConstants.appType ? (
                <Error />
              ) : (
                <CategoryList type={'Accounts'} />
              ),
            },
            {
              path: 'account-category/:categoryID',
              element: AppConstants.appType ? (
                <Error />
              ) : (
                <CategoryDetail categoryType={'Accounts'} />
              ),
            },
            {
              path: 'account-attributes',
              element: AppConstants.appType ? (
                <Error />
              ) : (
                <AttributesList type={'Accounts'} />
              ),
            },
            {
              path: 'account-configs',
              element: AppConstants.appType ? <Error /> : <AccountSetting />,
            },
            {
              path: 'listings',
              element: <MainListings />,
            },
            {
              path: 'listing/:listingID',
              element: <ListingDetail2 />,
            },
            {
              path: 'edit-listing/:listingID',
              element: <AddListing isEdit={true} />,
            },
            {
              path: 'add-listing',
              // Keep old bookmarks working, but route them to the canonical
              // listing creation flow instead of rendering the legacy form.
              element: <LegacyAddListingRedirect />,
            },
            {
              // Temporary rollback path for the legacy listing form. It is not
              // linked from the current navigation.
              path: 'legacy-add-listing',
              element: AppConstants.appType ? (
                <AddListings />
              ) : AppConstants.accessControl?.[
                  AppConstants.plan_name
                ]?.includes(RestrictedFeatureEnum.createListing) ? (
                <Error />
              ) : (
                <AddListings />
              ),
            },
            {
              path: 'create-listing',
              element: AppConstants.appType ? (
                <AddListing />
              ) : AppConstants.accessControl?.[
                  AppConstants.plan_name
                ]?.includes(RestrictedFeatureEnum.createListing) ? (
                <Error />
              ) : (
                <AddListing />
              ),
            },

            {
              path: 'add-digital',
              element: AppConstants.appType ? (
                <AddListings type={'digital'} />
              ) : AppConstants.accessControl?.[
                  AppConstants.plan_name
                ]?.includes(RestrictedFeatureEnum.createListing) ? (
                <Error />
              ) : (
                <AddListings type={'digital'} />
              ),
            },
            {
              path: 'add-audio',
              // Audio users should be able to complete the form and upload
              // their content before seeing the paid-plan restriction on the
              // final Create Listing action.
              element: <AddListing />,
            },
            {
              path: 'listing-category',
              element: <CategoryList2 type={'Listings'} />,
              children: [
                {
                  path: 'translation',
                  element: <CategoryDescriptionTranslate />,
                },
              ],
            },
            {
              path: 'listing-category/:categoryID',
              element: <CategoryDetail />,
            },
            {
              path: 'add-listing-category',
              element: <AddCategory2 type={'Listings'} />,
            },
            {
              path: 'add-account-category',
              element: AppConstants.appType ? (
                <Error />
              ) : (
                <AddCategory2 type={'Accounts'} />
              ),
            },
            {
              path: 'listing-attributes',
              element: <AttributesList type={'Listings'} />,
            },
            {
              path: 'attribute-groups',
              element: <AttributesGroups type={'Listings'} />,
            },
            {
              path: 'add-attribute',
              element: <AddAttributes2 />,
            },
            {
              path: 'edit-attribute/:attributeID',
              element: <AddAttributes2 isEdit={true} />,
            },

            //
            {
              path: 'cart-attributes',
              element: <AttributesList type={'cart_attributes'} />,
            },
            {
              path: 'attribute-groups',
              element: <AttributesGroups type={'cart_attributes'} />,
            },

            //
            {
              path: 'listing-configs',
              element: <ListingsConfiguration />,
            },
            {
              path: 'listing-variants',
              element: <ListingVariantType type={'Listings'} />,
            },
            {
              path: 'variant-type/:id',
              element: <VariantTypeDetail />,
            },
            {
              path: 'collections',
              element: <Collections />,
            },
            {
              path: 'create-collection',
              element: <CreateCollection2 />,
            },
            {
              path: 'edit-collection/:collectionID',
              element: <CreateCollection2 isEdit={true} />,
            },
            {
              path: 'collection/:id',
              element: <CollectionDetail />,
            },
            {
              path: 'promotions',
              element: <PromoBanners />,
            },
            {
              path: 'add-promotion',
              element: <AddPromoBanner />,
            },
            {
              path: 'edit-promotion/:promoID',
              element: <AddPromoBanner isEdit={true} />,
            },
            {
              path: 'coupon',
              element: <Coupons />,
            },
            {
              path: 'coupon/:couponID',
              element: <CouponDetail />,
            },
            {
              path: 'users',
              element: <Users />,
            },
            {
              path: 'user/:userID',
              element: <UserDetail2 />,
            },
            {
              path: 'user-attributes',
              element: <AttributesList type={'Users'} />,
            },
            {
              path: 'media',
              element: <MediaManagers />,
            },
            {
              path: 'review-ratings',
              element: <ReviewRatings />,
            },
            {
              path: 'reported-listings',
              element: <ReportedListings />,
            },
            // {
            //   path: 'reported-accounts',
            //   element: <ReportedAccounts />,
            // },
            {
              path: 'settings',
              element: <Setting />,
            },
            {
              path: 'delivery',
              element: <ShippingMethod />,
            },
            {
              path: 'payments',
              element: <PaymentMethod />,
            },
            {
              path: 'commission',
              element: <Commision />,
            },
            {
              path: 'rewards',
              element: <RewardProgramsPage />,
            },
            {
              path: 'rewards/:programId',
              element: <RewardProgramDetailPage />,
            },
            {
              path: 'reward-programs',
              element: <RewardProgramsPage />,
            },
            {
              path: 'bundles',
              element: <BundlesPage />,
            },
            {
              path: 'subscription',
              element: <SubscriptionLayout />,
            },
            {
              path: 'subscription/:id',
              element: <SubscriptionDetail />,
            },
            {
              path: 'membership',
              element: <MembershipLayout />,
            },
            {
              path: 'whatsapp',
              element: <WhatsAppSettingsPage />,
            },
            {
              path: 'extension',
              element: <ExtensionLayout />,
            },

            {
              path: 'taxes',
              element: <Taxes />,
            },
            {
              path: 'special-fees',
              element: <SpecialFees />,
            },
            {
              path: 'languages',
              element: <LanguageLists />,
            },
            {
              path: 'currency',
              element: <Currencies />,
            },
            {
              path: 'countries',
              element: <AppCountry />,
            },
            {
              path: 'api',
              element: <Developers />,
            },
            {
              path: 'mcp-connections',
              element: <McpConnectionsPage />,
            },
            {
              path: 'mcp-logs',
              element: <McpLogsPage />,
            },
            {
              path: 'opensource-starter-kits',
              element: <OpenSourceStarterKitsPage />,
            },
            {
              path: 'people',
              element: <PeoplePage />,
            },
            {
              path: 'workforce',
              element: <WorkforcePage />,
            },
            {
              path: 'payroll',
              element: <PayrollPage />,
            },
            {
              path: 'pay-components',
              element: <PayrollPage initialTab="components" />,
            },
            {
              path: 'expenses',
              element: <ExpensesPage />,
            },
            {
              path: 'recurring-payments',
              element: <RecurringPaymentsPage />,
            },
            {
              path: 'auth',
              element: <Auth />,
            },
            {
              path: 'checkouts',
              element: <Checkouts />,
            },
            {
              path: 'manage_lfn',
              element: <ManageLeftNavBarConfiguration />,
            },
            {
              path: 'audit-logs',
              element: <ComplianceAuditLogs />,
            },

            {
              path: 'test',
              element: <ParentTest />,
              children: [{path: 'test-c1', element: <Test />}],
            },
            {
              path: 'apps/social-connect',
              element: <Navigate to="/social" replace />,
            },
            {
              path: 'apps/:appKey',
              element: <IntegrationPageWrapper />,
            },
            // Legacy address for iframe apps; Marketing was the original app.
            {
              path: 'workflow',
              element: <Navigate to="/apps/marketing" replace />,
            },
            {
              path: 'automation-rules',
              element: <AutomationRulesPage />,
            },
            {
              path: 'sentry-check',
              element: <SentryCheck />,
            },
            // Isolated preview only; intentionally not linked from the main page/menu.
            {
              path: 'gamification-test',
              element: <GamificationTestPage />,
            },
            // Isolated CMS preview; intentionally not linked from the main page/menu.
            {
              path: 'cms-test',
              element: <CmsTestPage />,
            },
            // Temporarily disabled: sidebar links open the stable Tradly pages.
            // {
            //   path: 'workspace/templates',
            //   element: <WorkspaceCatalogPage kind="templates" />,
            // },
            // {
            //   path: 'workspace/integrations',
            //   element: <WorkspaceCatalogPage kind="integrations" />,
            // },
            {
              path: 'cms-add-page',
              element: <CMSAddPage />,
            },
            {
              path: 'cms',
              element: <CMSListPage />,
            },
            {
              path: 'cms/p/add_page',
              element: <CMSAddPage contentType="page" />,
            },
            {
              path: 'cms/blog/add_blogpost',
              element: <CMSAddPage contentType="blog" />,
            },
            {
              path: 'cms/docs/add_docs',
              element: <CMSAddPage contentType="docs" />,
            },
            // Legacy Icebreaker-style paths kept as aliases.
            {
              path: 'workspace/p/add_page',
              element: <CMSAddPage contentType="page" />,
            },
            {
              path: 'workspace/blog/add_blogpost',
              element: <CMSAddPage contentType="blog" />,
            },
            {
              path: 'workspace/docs/add_docs',
              element: <CMSAddPage contentType="docs" />,
            },
            {
              path: 'cms-list',
              element: <CMSListPage />,
            },
            {
              path: 'cms/p',
              element: <CMSListPage contentType="page" />,
            },
            {
              path: 'cms/blog',
              element: <CMSListPage contentType="blog" />,
            },
            {
              path: 'cms/docs',
              element: <CMSListPage contentType="docs" />,
            },
            {
              path: 'workspace/p',
              element: <CMSListPage contentType="page" />,
            },
            {
              path: 'workspace/blog',
              element: <CMSListPage contentType="blog" />,
            },
            {
              path: 'workspace/docs',
              element: <CMSListPage contentType="docs" />,
            },
            {
              path: 'cms-edit/:id',
              element: <CMSEditPage />,
            },
            // Isolated preview for the next shared feature-restriction panel.
            {
              path: 'upgrade-preview',
              element: <UpgradePreview />,
            },
            {
              path: 'upgrade',
              element: <UpgradePage />,
            },
            // Isolated usage dashboard for comparing the new breakdown layout.
            {
              path: 'usage-breakdown-preview',
              element: <UsageBreakdown />,
            },
          ],
        },
      ],
    },
  ]);

  // Keep the current path in state so the gate reacts to in-app navigation.
  useEffect(
    () => router.subscribe(state => setCurrentPath(state.location.pathname)),
    [router],
  );

  // À la carte workspaces pay through credits, so they are never blocked.
  const mustChoosePlan =
    requiresPlanSelection &&
    AppConstants.tenant_metadata?.product_model !== 'ala_carte' &&
    !PLAN_GATE_EXEMPT_PATHS.includes(currentPath.replace(/\/+$/, ''));

  return reload ? (
    <>
      <RouterProvider router={router} />
      {mustChoosePlan && (
        <FeatureUpgradePanel
          isOpen
          blocking
          onClose={() => setRequiresPlanSelection(false)}
        />
      )}
    </>
  ) : (
    <SafeAreaView style={styles.container}>
      <Appload enable={isVisible} />
    </SafeAreaView>
  );
};

export default AllRoutes;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: AppColor.AppLightGreen,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
